/**
 * Typed client for the predictive-estimating surface (WO-D4 backend, WO-D5 UX).
 *
 * Routes (beam-backend domainRouter.ts):
 *   GET  /api/org/trades                       → OrgTradesDto
 *   PUT  /api/org/trades        { tradeIds }    → OrgTradesDto
 *   GET  /api/services/suggested?limit=         → SuggestedServicesResponse
 *   GET  /api/materials/suggested?limit=        → SuggestedMaterialsResponse  (WO-D5)
 *   POST /api/estimates/:id/add-service { serviceTemplateId } → AddServiceResponse
 *
 * `GET /api/trades` (the GLOBAL catalog of selectable trades) is also surfaced here for the
 * multi-trade selector's "available trades" list.
 *
 * All shapes from @beam/api-types — never re-declared here. Responses parsed with the zod schemas
 * so the app fails loudly on contract drift. Bearer token attached by src/api/client.ts.
 * No PII in URLs (CLAUDE.md §2). NEVER imports @smartbox/* (mobile CLAUDE.md invariant 1).
 */

import { z } from 'zod';
import { get, post, put } from './client';
import {
  OrgTradesDto,
  SuggestedServicesResponse,
  SuggestedMaterialsResponse,
  AddServiceResponse,
  SetOrgTradesRequest,
} from '@beam/api-types';
import type {
  OrgTradeDto,
  SuggestedServiceDto,
  SuggestedMaterialDto,
  AddServiceResponse as AddServiceResponseType,
} from '@beam/api-types';

// The GLOBAL trade catalog (selectable trades). The backend returns raw trade rows; we only need
// id/code/name for the selector, so parse a tolerant shape locally (per the raw-row mapping rule).
const AvailableTrade = z.object({
  id: z.string(),
  code: z.string(),
  name: z.string(),
});
export type AvailableTrade = z.infer<typeof AvailableTrade>;

/** The catalog of trades the org can select from (GLOBAL `trade` table). */
export async function listAvailableTrades(): Promise<AvailableTrade[]> {
  const raw = await get<unknown>('/api/trades');
  return (raw as unknown[]).map((item) => AvailableTrade.parse(item));
}

/** The org's currently selected trades. */
export async function getOrgTrades(): Promise<OrgTradeDto[]> {
  const raw = await get<unknown>('/api/org/trades');
  return OrgTradesDto.parse(raw).trades;
}

/** Replace the org's trade selection (set semantics; first = primary; none allowed). */
export async function setOrgTrades(tradeIds: string[]): Promise<OrgTradeDto[]> {
  const body = SetOrgTradesRequest.parse({ tradeIds });
  const raw = await put<unknown>('/api/org/trades', body);
  return OrgTradesDto.parse(raw).trades;
}

/** Ranked curated services for the org's trades (the "+ Add service" picker). */
export async function listSuggestedServices(limit = 20): Promise<SuggestedServiceDto[]> {
  const raw = await get<unknown>(`/api/services/suggested?limit=${encodeURIComponent(String(limit))}`);
  return SuggestedServicesResponse.parse(raw).services;
}

/** Ranked recommended materials for the org (the "+ Add material" quick-pick). */
export async function listSuggestedMaterials(limit = 20): Promise<SuggestedMaterialDto[]> {
  const raw = await get<unknown>(`/api/materials/suggested?limit=${encodeURIComponent(String(limit))}`);
  return SuggestedMaterialsResponse.parse(raw).materials;
}

/** Drop a curated service into an estimate as editable, snapshotted material+labor lines. */
export async function addServiceToEstimate(
  estimateId: string,
  serviceTemplateId: string,
): Promise<AddServiceResponseType> {
  const raw = await post<unknown>(`/api/estimates/${estimateId}/add-service`, { serviceTemplateId });
  return AddServiceResponse.parse(raw);
}
